import { useState, useCallback, useRef, useEffect } from "react";
import { authedFetch } from "../../common/http_service";

const apiBase = () => (window.ideaLibraryRootUrl || "").replace(/\/$/, "");

// Same middleware endpoints the console's SubtitlesModal/useTrainingDetail
// hook talks to — hit directly here since the editor has no Next.js proxy
// layer. Job completion is tracked by polling (console also runs a socket
// alongside its own polling, but the editor has no socket channel wired for
// this job type — polling alone is what's actually load-bearing there too).
export function useSubtitles(trainingId) {
  const [sceneAudios, setSceneAudios] = useState(null);
  const [sceneAudiosLoading, setSceneAudiosLoading] = useState(false);

  const [subtitleLanguages, setSubtitleLanguages] = useState([]);
  const [subtitleTotalScenes, setSubtitleTotalScenes] = useState(0);
  const [subtitleLanguagesLoading, setSubtitleLanguagesLoading] = useState(false);
  // The language the visor turns on from the CC button; null = none.
  const [defaultSubtitleLanguage, setDefaultSubtitleLanguageState] = useState(null);
  const [isSavingDefault, setIsSavingDefault] = useState(false);

  const [sceneSubtitle, setSceneSubtitle] = useState({});
  const [sceneSubtitleLoading, setSceneSubtitleLoading] = useState({});
  const [sceneSubtitleNotFound, setSceneSubtitleNotFound] = useState({});

  const [isGeneratingSubtitle, setIsGeneratingSubtitle] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(0);

  const [isDeletingSubtitle, setIsDeletingSubtitle] = useState(false);

  const pollingRef = useRef(null);

  const clearPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const fetchSceneAudios = useCallback(async () => {
    if (!trainingId) return;
    setSceneAudiosLoading(true);
    try {
      const res = await authedFetch(`${apiBase()}/api/studio/trainings/${trainingId}/scene-audios`);
      if (res.ok) setSceneAudios(await res.json());
    } catch {
      /* silent — scene list is optional, just won't show the per-scene viewer */
    } finally {
      setSceneAudiosLoading(false);
    }
  }, [trainingId]);

  const fetchSubtitleLanguages = useCallback(async () => {
    if (!trainingId) return;
    setSubtitleLanguagesLoading(true);
    try {
      const res = await authedFetch(`${apiBase()}/api/studio/subtitle/${trainingId}/languages`);
      if (res.ok) {
        const data = await res.json();
        setSubtitleLanguages(data.availableLanguages || []);
        setSubtitleTotalScenes(data.totalSceneAudioFiles || 0);
        setDefaultSubtitleLanguageState(data.defaultLanguage || null);
      }
    } catch {
      /* silent */
    } finally {
      setSubtitleLanguagesLoading(false);
    }
  }, [trainingId]);

  const fetchSceneSubtitle = useCallback(
    async (navigationId, language) => {
      if (!trainingId) return;
      const key = `${navigationId}_${language}`;
      setSceneSubtitleLoading((prev) => ({ ...prev, [key]: true }));
      try {
        const res = await authedFetch(
          `${apiBase()}/api/studio/subtitle/${trainingId}/${navigationId}/${language}`
        );
        if (res.ok) {
          const data = await res.json();
          setSceneSubtitle((prev) => ({ ...prev, [key]: data }));
          setSceneSubtitleNotFound((prev) => ({ ...prev, [key]: false }));
        } else {
          setSceneSubtitleNotFound((prev) => ({ ...prev, [key]: true }));
        }
      } catch {
        setSceneSubtitleNotFound((prev) => ({ ...prev, [key]: true }));
      } finally {
        setSceneSubtitleLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [trainingId]
  );

  const startPolling = useCallback(
    (jobId) => {
      clearPolling();
      pollingRef.current = setInterval(async () => {
        try {
          const res = await authedFetch(`${apiBase()}/api/jobs/${jobId}`);
          const data = await res.json();
          if (data.status === "completed") {
            clearPolling();
            setIsGeneratingSubtitle(false);
            setGenerateProgress(100);
            fetchSubtitleLanguages();
            setTimeout(() => setGenerateProgress(0), 2000);
          } else if (data.status === "failed") {
            clearPolling();
            setIsGeneratingSubtitle(false);
            setGenerateProgress(0);
          } else if (typeof data.progress === "number") {
            setGenerateProgress(data.progress);
          }
        } catch {
          /* network hiccup — keep polling */
        }
      }, 3000);
    },
    [fetchSubtitleLanguages]
  );

  const generateSubtitle = useCallback(
    async (language, forceCreate) => {
      if (!trainingId || !language) return { success: false, message: "Missing parameters" };

      setIsGeneratingSubtitle(true);
      setGenerateProgress(0);

      try {
        const res = await authedFetch(`${apiBase()}/api/studio/subtitle/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idealStudioTrainingId: trainingId, language, forceCreate }),
        });
        const data = await res.json();

        if (res.status === 202 && data.jobId) {
          startPolling(data.jobId);
          return { success: true };
        }
        if (res.status === 409) {
          setIsGeneratingSubtitle(false);
          return {
            success: false,
            message:
              data.message || "Subtitles already exist. Enable force regenerate to overwrite.",
          };
        }
        setIsGeneratingSubtitle(false);
        return { success: false, message: data.message || "Failed to start subtitle generation" };
      } catch {
        setIsGeneratingSubtitle(false);
        return { success: false, message: "Network error" };
      }
    },
    [trainingId, startPolling]
  );

  const deleteSubtitleLanguage = useCallback(
    async (language) => {
      if (!trainingId || !language) return { success: false, message: "Missing parameters" };

      setIsDeletingSubtitle(true);
      try {
        const res = await authedFetch(
          `${apiBase()}/api/studio/subtitle/${trainingId}/${language}`,
          {
            method: "DELETE",
          }
        );
        if (res.ok) {
          await fetchSubtitleLanguages();
          return { success: true };
        }
        const data = await res.json().catch(() => null);
        return { success: false, message: data?.message || "Failed to delete subtitles" };
      } catch {
        return { success: false, message: "Network error" };
      } finally {
        setIsDeletingSubtitle(false);
      }
    },
    [trainingId, fetchSubtitleLanguages]
  );

  // `language` null clears the default. Only languages the training already
  // has are accepted server-side.
  const setDefaultSubtitleLanguage = useCallback(
    async (language) => {
      if (!trainingId) return { success: false, message: "Missing parameters" };

      setIsSavingDefault(true);
      try {
        const res = await authedFetch(`${apiBase()}/api/studio/subtitle/${trainingId}/default`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ language: language || null }),
        });
        if (res.ok) {
          const data = await res.json().catch(() => null);
          setDefaultSubtitleLanguageState(data?.defaultLanguage ?? (language || null));
          return { success: true };
        }
        const data = await res.json().catch(() => null);
        return { success: false, message: data?.message };
      } catch {
        return { success: false, message: "Network error" };
      } finally {
        setIsSavingDefault(false);
      }
    },
    [trainingId]
  );

  const reset = useCallback(() => {
    clearPolling();
    setSceneAudios(null);
    setSceneAudiosLoading(false);
    setSubtitleLanguages([]);
    setSubtitleTotalScenes(0);
    setSubtitleLanguagesLoading(false);
    setDefaultSubtitleLanguageState(null);
    setIsSavingDefault(false);
    setSceneSubtitle({});
    setSceneSubtitleLoading({});
    setSceneSubtitleNotFound({});
    setIsGeneratingSubtitle(false);
    setGenerateProgress(0);
    setIsDeletingSubtitle(false);
  }, []);

  useEffect(() => () => clearPolling(), []);

  return {
    sceneAudios,
    sceneAudiosLoading,
    fetchSceneAudios,
    subtitleLanguages,
    subtitleTotalScenes,
    subtitleLanguagesLoading,
    fetchSubtitleLanguages,
    defaultSubtitleLanguage,
    isSavingDefault,
    setDefaultSubtitleLanguage,
    sceneSubtitle,
    sceneSubtitleLoading,
    sceneSubtitleNotFound,
    fetchSceneSubtitle,
    isGeneratingSubtitle,
    generateProgress,
    generateSubtitle,
    isDeletingSubtitle,
    deleteSubtitleLanguage,
    reset,
  };
}
